import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeader } from '../utils/auth'; 
import { useNotification } from '../context/NotificationContext'; 
import './ImageManagement.css';

function ImageManagement() {
    const [images, setImages] = useState([]);
    const [hotels, setHotels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { showNotification } = useNotification();

    const [newImageFile, setNewImageFile] = useState(null);
    const [selectedHotelId, setSelectedHotelId] = useState('');
    const [fileInputKey, setFileInputKey] = useState(Date.now());

    // This useCallback is for auth headers, it's fine.
    

    console.log('Local Storage Token:', localStorage.getItem('token'));
console.log('Auth Header Object:', getAuthHeader());

    const fetchInitialData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Use environment variables for all API URLs
            const hotelsApiUrl = `${process.env.REACT_APP_API_URL}/api.php?path=hotels&type=Propio&limit=999`;
            const imagesApiUrl = `${process.env.REACT_APP_API_URL}/api.php?path=images`;

            const [hotelsResponse, imagesResponse] = await Promise.all([
                fetch(hotelsApiUrl, { headers: getAuthHeader() }),
                fetch(imagesApiUrl, { headers: getAuthHeader() })
            ]);

            if (!hotelsResponse.ok) throw new Error('Failed to fetch hotels');
            if (!imagesResponse.ok) throw new Error('Failed to fetch images');

            const hotelsData = await hotelsResponse.json();
            const imagesData = await imagesResponse.json();

            if (hotelsData.success && hotelsData.data?.hotels) {
                setHotels(hotelsData.data.hotels);
                if (hotelsData.data.hotels.length > 0) {
                    setSelectedHotelId(hotelsData.data.hotels[0].hotel_id);
                }
            } else {
                throw new Error(hotelsData.error?.message || 'Error loading hotels');
            }

            // The image 'url' property from the backend is already the full URL
            if (imagesData.success && Array.isArray(imagesData.data)) {
                setImages(imagesData.data);
            } else {
                throw new Error(imagesData.error?.message || 'Error loading images');
            }
        } catch (err) {
            setError(err.message);
            showNotification(`Error fetching initial data: ${err.message}`, 'error');
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader, showNotification]);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    const handleFileChange = (e) => {
        setNewImageFile(e.target.files[0]);
    };

    const handleImageUpload = async (e) => {
        e.preventDefault();
        if (!newImageFile || !selectedHotelId) {
            showNotification('Please select a hotel and an image file.', 'warning');
            return;
        }
        setLoading(true);
        const formData = new FormData();
        formData.append('image', newImageFile);
        formData.append('hotel_id', selectedHotelId);

        try {
            const apiUrl = `${process.env.REACT_APP_API_URL}/api.php?path=images`;
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { ...getAuthHeader() }, // No 'Content-Type' needed for FormData
                body: formData,
            });
            const result = await response.json();
            if (response.ok && result.success) {
                showNotification(result.data.message || 'Image uploaded successfully!', 'success');
                setNewImageFile(null);
                setFileInputKey(Date.now());
                // The backend now returns the full image object including the new URL
                setImages(prevImages => [result.data.image, ...prevImages]);
            } else {
                throw new Error(result.error?.message || 'Failed to upload image.');
            }
        } catch (err) {
            setError(err.message);
            showNotification(`Error uploading image: ${err.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteImage = async (imageId) => {
        if (!window.confirm('Are you sure you want to delete this image?')) return;
        setLoading(true);
        try {
            const apiUrl = `${process.env.REACT_APP_API_URL}/api.php?path=images/${imageId}`;
            const response = await fetch(apiUrl, {
                method: 'DELETE',
                headers: getAuthHeader(),
            });
            const result = await response.json();
            if (response.ok && result.success) {
                showNotification(result.data.message || 'Image deleted successfully!', 'success');
                setImages(prevImages => prevImages.filter(img => img.image_id !== imageId));
            } else {
                throw new Error(result.error?.message || 'Failed to delete image.');
            }
        } catch (err) {
            setError(err.message);
            showNotification(`Error deleting image: ${err.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    if (loading && images.length === 0) {
        return (
            <div className="image-management-container">
                <h2>Gestión de Imágenes</h2>
                <p>Cargando datos...</p>
            </div>
        );
    }

    return (
        <div className="image-management-container">
            <h2>Gestión de Imágenes (Solo Super Admin)</h2>
            <div className="image-upload-section card">
                <h3>Subir Nueva Imagen</h3>
                <form onSubmit={handleImageUpload}>
                    <input 
                        key={fileInputKey}
                        type="file" 
                        accept="image/*" 
                        onChange={handleFileChange}
                    />
                    <select
                        value={selectedHotelId}
                        onChange={(e) => setSelectedHotelId(e.target.value)}
                        required
                    >
                        <option value="">Selecciona un Hotel (Propio)</option>
                        {hotels.map(hotel => (
                            <option key={hotel.hotel_id} value={hotel.hotel_id}>
                                {hotel.name} ({hotel.city})
                            </option>
                        ))}
                    </select>
                    <button type="submit" disabled={loading || !newImageFile}>
                        {loading ? 'Subiendo...' : 'Subir Imagen'}
                    </button>
                </form>
            </div>
            <div className="image-list-section card">
                <h3>Imágenes Existentes</h3>
                {error && <p style={{ color: 'red' }}>Error: {error}</p>}
                {images.length === 0 && !loading ? (
                    <p>No hay imágenes disponibles.</p>
                ) : (
                    <div className="image-grid">
                        {images.map((image) => (
                            <div key={image.image_id} className="image-item">
                                <img 
                                    src={image.url}
                                    alt={`For hotel ${image.hotel_id}`} 
                                    className="thumbnail"
                                    onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/150x100/CCCCCC/666666?text=Error" }}
                                />
                                <p>ID: {image.image_id}</p>
                                {image.hotel_id && (
                                    <p>
                                        Hotel: { hotels.find(h => h.hotel_id == image.hotel_id)?.name || image.hotel_id }
                                    </p>
                                )}
                                <button onClick={() => handleDeleteImage(image.image_id)} disabled={loading}>
                                    Eliminar
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default ImageManagement;